import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CityAutocomplete } from "@/components/ui/city-autocomplete";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Package, ArrowRight, CheckCircle, Loader2, MessageCircle } from "lucide-react";

const FORM_STORAGE_KEY = "gobledpro_transport_form_draft";
const WHATSAPP_NUMBER = "33600000000"; // À remplacer par le vrai numéro

const formSchema = z.object({
  sender_name: z.string().min(2, "Le nom doit contenir au moins 2 caractères").max(100),
  sender_email: z.string().email("Email invalide").max(255),
  sender_phone: z.string().min(10, "Numéro de téléphone invalide").max(20),
  departure_city: z.string().min(2, "Ville requise").max(100),
  departure_country: z.string().min(2, "Pays requis").max(100),
  arrival_city: z.string().min(2, "Ville requise").max(100),
  arrival_country: z.string().min(2, "Pays requis").max(100),
  package_type: z.string().min(1, "Type de colis requis"),
  package_description: z.string().max(500).optional(),
  package_weight: z.string().max(50).optional(),
  package_dimensions: z.string().max(100).optional(),
  preferred_date: z.string().optional(),
  notes: z.string().max(1000).optional(),
});

type FormData = z.infer<typeof formSchema>;

const TransportRequest = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      departure_country: "France",
      arrival_country: "Maroc",
    },
  });

  // Watch all form values for localStorage backup
  const formValues = watch();

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    const dataToSave = {
      sender_name: formValues.sender_name,
      sender_email: formValues.sender_email,
      sender_phone: formValues.sender_phone,
      departure_city: formValues.departure_city,
      departure_country: formValues.departure_country,
      arrival_city: formValues.arrival_city,
      arrival_country: formValues.arrival_country,
      package_type: formValues.package_type,
      package_description: formValues.package_description,
      package_weight: formValues.package_weight,
      package_dimensions: formValues.package_dimensions,
      preferred_date: formValues.preferred_date,
      notes: formValues.notes,
    };
    if (Object.values(dataToSave).some(v => v)) {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(dataToSave));
    }
  }, [formValues]);

  // Restore form data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem(FORM_STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        Object.entries(parsed).forEach(([key, value]) => {
          if (value) {
            setValue(key as keyof FormData, value as string);
          }
        });
      } catch (e) {
        console.error("Error parsing saved form data:", e);
      }
    }
  }, [setValue]);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Bonjour, j'ai une question concernant ma demande de transport sur GoBledPro.");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("transport_requests").insert({
        sender_name: data.sender_name,
        sender_email: data.sender_email,
        sender_phone: data.sender_phone,
        departure_city: data.departure_city,
        departure_country: data.departure_country,
        arrival_city: data.arrival_city,
        arrival_country: data.arrival_country,
        package_type: data.package_type,
        package_description: data.package_description || null,
        package_weight: data.package_weight || null,
        package_dimensions: data.package_dimensions || null,
        preferred_date: data.preferred_date || null,
        notes: data.notes || null,
        user_id: null, // Beta: pas d'authentification requise
      });
      if (error) throw error;

      // Clear saved form data on success
      localStorage.removeItem(FORM_STORAGE_KEY);

      setIsSuccess(true);
      toast({
        title: "Demande envoyée !",
        description: "Nous avons bien reçu votre demande. Nous vous contacterons rapidement.",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 section-padding">
          <div className="container mx-auto max-w-2xl text-center">
            <div className="bg-card border border-border rounded-3xl p-8 md:p-12">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary/10 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-secondary" />
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Demande envoyée !
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Merci pour votre demande. Notre équipe va l'analyser et vous mettre en relation
                avec un transporteur adapté dans les plus brefs délais.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate("/")}>
                  Retour à l'accueil
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show transport form directly (no auth required for beta)
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 section-padding">
        <div className="container mx-auto max-w-3xl">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              Envoyez votre colis en toute confiance
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Remplissez ce formulaire et nous vous mettrons en relation avec un transporteur vérifié adapté à vos besoins.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="bg-card border border-border rounded-3xl p-6 md:p-8 space-y-8">
            {/* Contact Info */}
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-4 pb-2 border-b border-border">
                Vos coordonnées
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sender_name">Nom complet *</Label>
                  <Input
                    id="sender_name"
                    placeholder="Jean Dupont"
                    {...register("sender_name")}
                    className={errors.sender_name ? "border-destructive" : ""}
                  />
                  {errors.sender_name && (
                    <p className="text-sm text-destructive">{errors.sender_name.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sender_email">Email *</Label>
                  <Input
                    id="sender_email"
                    type="email"
                    placeholder="jean@email.com"
                    {...register("sender_email")}
                    className={errors.sender_email ? "border-destructive" : ""}
                  />
                  {errors.sender_email && (
                    <p className="text-sm text-destructive">{errors.sender_email.message}</p>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="sender_phone">Téléphone *</Label>
                  <Input
                    id="sender_phone"
                    placeholder="+33 6 12 34 56 78"
                    {...register("sender_phone")}
                    className={errors.sender_phone ? "border-destructive" : ""}
                  />
                  {errors.sender_phone && (
                    <p className="text-sm text-destructive">{errors.sender_phone.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Departure */}
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-4 pb-2 border-b border-border">
                Lieu de départ
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="departure_city">Ville *</Label>
                  <CityAutocomplete
                    id="departure_city"
                    placeholder="Paris"
                    value={watch("departure_city") || ""}
                    onChange={(value) => setValue("departure_city", value)}
                    className={errors.departure_city ? "border-destructive" : ""}
                    country={watch("departure_country") || "France"}
                  />
                  {errors.departure_city && (
                    <p className="text-sm text-destructive">{errors.departure_city.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="departure_country">Pays *</Label>
                  <Select
                    defaultValue="France"
                    onValueChange={(value) => setValue("departure_country", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un pays" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="France">France</SelectItem>
                      <SelectItem value="Belgique">Belgique</SelectItem>
                      <SelectItem value="Espagne">Espagne</SelectItem>
                      <SelectItem value="Allemagne">Allemagne</SelectItem>
                      <SelectItem value="Pays-Bas">Pays-Bas</SelectItem>
                      <SelectItem value="Italie">Italie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Arrival */}
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-4 pb-2 border-b border-border">
                Lieu d'arrivée
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="arrival_city">Ville *</Label>
                  <CityAutocomplete
                    id="arrival_city"
                    placeholder="Casablanca"
                    value={watch("arrival_city") || ""}
                    onChange={(value) => setValue("arrival_city", value)}
                    className={errors.arrival_city ? "border-destructive" : ""}
                    country={watch("arrival_country") || "Maroc"}
                  />
                  {errors.arrival_city && (
                    <p className="text-sm text-destructive">{errors.arrival_city.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="arrival_country">Pays *</Label>
                  <Select
                    defaultValue="Maroc"
                    onValueChange={(value) => setValue("arrival_country", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un pays" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Maroc">Maroc</SelectItem>
                      <SelectItem value="France">France</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Package */}
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-4 pb-2 border-b border-border">
                Informations sur le colis
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="package_type">Type de colis *</Label>
                  <Select onValueChange={(value) => setValue("package_type", value)}>
                    <SelectTrigger className={errors.package_type ? "border-destructive" : ""}>
                      <SelectValue placeholder="Sélectionner le type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="carton">Carton(s)</SelectItem>
                      <SelectItem value="valise">Valise(s)</SelectItem>
                      <SelectItem value="palette">Palette</SelectItem>
                      <SelectItem value="meuble">Meuble</SelectItem>
                      <SelectItem value="electromenager">Électroménager</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.package_type && (
                    <p className="text-sm text-destructive">{errors.package_type.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="package_weight">Poids estimé</Label>
                  <Input
                    id="package_weight"
                    placeholder="Ex: 20 kg"
                    {...register("package_weight")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="package_dimensions">Dimensions</Label>
                  <Input
                    id="package_dimensions"
                    placeholder="Ex: 50x40x30 cm"
                    {...register("package_dimensions")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_date">Date souhaitée</Label>
                  <Input
                    id="preferred_date"
                    type="date"
                    {...register("preferred_date")}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="package_description">Description du colis</Label>
                  <Textarea
                    id="package_description"
                    placeholder="Décrivez votre colis (contenu, fragilité, etc.)"
                    {...register("package_description")}
                    rows={3}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="notes">Notes supplémentaires</Label>
                  <Textarea
                    id="notes"
                    placeholder="Informations complémentaires pour le transporteur"
                    {...register("notes")}
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <Button
                type="submit"
                size="lg"
                className="w-full text-lg py-6"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    Envoyer ma demande
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              <p className="text-center text-sm text-muted-foreground mt-4">
                En envoyant ce formulaire, vous acceptez nos{" "}
                <a href="/cgu" className="text-muted-foreground underline hover:text-foreground">
                  CGU
                </a>
              </p>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TransportRequest;
